import { z } from 'zod'

export const AccountSplitStatus = z.enum([
  'draft',
  'dry_run_ok',
  'executing',
  'partial_failure',
  'completed',
  'aborted'
])
export type AccountSplitStatus = z.infer<typeof AccountSplitStatus>

export const AccountSplitTargetMode = z.enum(['existing_account', 'new_account'])
export type AccountSplitTargetMode = z.infer<typeof AccountSplitTargetMode>

export const AccountSplitItemAssetType = z.enum([
  'subscription',
  'domain',
  'handle',
  'hosting_site',
  'mailbox'
])
export type AccountSplitItemAssetType = z.infer<typeof AccountSplitItemAssetType>

export const AccountSplitItemStatus = z.enum([
  'pending',
  'in_progress',
  'succeeded',
  'failed',
  'skipped'
])
export type AccountSplitItemStatus = z.infer<typeof AccountSplitItemStatus>

export const AccountSplitAccountRef = z.object({
  id: z.number().int(),
  email: z.string().optional()
})
export type AccountSplitAccountRef = z.infer<typeof AccountSplitAccountRef>

export const AccountSplitItem = z.object({
  id: z.number().int(),
  asset_type: AccountSplitItemAssetType,
  asset_id: z.number().int(),
  status: AccountSplitItemStatus,
  attempts: z.number().int(),
  error: z.string().nullable().optional(),
  before_snapshot: z.record(z.string(), z.any()).nullable().optional(),
  after_snapshot: z.record(z.string(), z.any()).nullable().optional(),
  started_at: z.number().int().nullable().optional(),
  finished_at: z.number().int().nullable().optional()
})
export type AccountSplitItem = z.infer<typeof AccountSplitItem>

export const DryRunResultItem = z.object({
  item_id: z.number().int(),
  asset_type: AccountSplitItemAssetType,
  asset_id: z.number().int(),
  planned_actions: z.array(z.string()),
  warnings: z.array(z.string())
})
export type DryRunResultItem = z.infer<typeof DryRunResultItem>

export const DryRunResultInvoiceProjection = z.object({
  invoice_id: z.string(),
  current_total_cents: z.number().int(),
  currency: z.string().optional(),
  after_split: z
    .array(
      z.object({
        customer: z.string(),
        total_cents: z.number().int()
      })
    )
    .optional(),
  note: z.string().optional()
})
export type DryRunResultInvoiceProjection = z.infer<typeof DryRunResultInvoiceProjection>

export const DryRunResultParty = z.object({
  account_id: z.number().int().nullable().optional(),
  stripe_customer_id: z.string().nullable().optional()
})
export type DryRunResultParty = z.infer<typeof DryRunResultParty>

export const DryRunResult = z.object({
  source: DryRunResultParty,
  target: DryRunResultParty,
  items: z.array(DryRunResultItem),
  open_invoices: z.array(DryRunResultInvoiceProjection).optional().default([]),
  warnings: z.array(z.string())
})
export type DryRunResult = z.infer<typeof DryRunResult>

export const AccountSplit = z.object({
  id: z.number().int(),
  status: AccountSplitStatus,
  target_mode: AccountSplitTargetMode,
  new_account_payload: z.record(z.string(), z.any()).nullable().optional(),
  notes: z.string().nullable().optional(),
  dry_run_result: DryRunResult.nullable().optional(),
  failure_reason: z.string().nullable().optional(),
  source_account: AccountSplitAccountRef.nullable().optional(),
  target_account: AccountSplitAccountRef.nullable().optional(),
  created_by: AccountSplitAccountRef.nullable().optional(),
  items: z.array(AccountSplitItem).optional().default([]),
  created: z.number().int(),
  updated: z.number().int(),
  executed_at: z.number().int().nullable().optional()
})
export type AccountSplit = z.infer<typeof AccountSplit>

export const AccountSplitListItem = z.object({
  id: z.number().int(),
  status: AccountSplitStatus,
  target_mode: AccountSplitTargetMode,
  source_account: AccountSplitAccountRef.nullable().optional(),
  target_account: AccountSplitAccountRef.nullable().optional(),
  created_by: AccountSplitAccountRef.nullable().optional(),
  created: z.number().int(),
  updated: z.number().int(),
  executed_at: z.number().int().nullable().optional()
})
export type AccountSplitListItem = z.infer<typeof AccountSplitListItem>

export const CreateAccountSplitRequest = z.object({
  source_account_id: z.number().int(),
  target_mode: AccountSplitTargetMode,
  target_account_id: z.number().int().optional(),
  new_account_payload: z.record(z.string(), z.any()).optional(),
  notes: z.string().optional()
})
export type CreateAccountSplitRequest = z.infer<typeof CreateAccountSplitRequest>

export const AddAccountSplitItemRequest = z.object({
  asset_type: AccountSplitItemAssetType,
  asset_id: z.number().int()
})
export type AddAccountSplitItemRequest = z.infer<typeof AddAccountSplitItemRequest>
